import { inject } from 'inversify'

import { InversionContainer } from '@/controller/container'
import { InjectionTokens } from '@/controller/tokens'

import { UseCase } from '@/domain/core/UseCase'
import { Coordinates } from '@/domain/protocols/Coordinates'
import { Result } from '@/domain/protocols/Result'

import { IHttpClient } from '@/infra/http/IHttpClient'
import { JetStreamRasterClient } from '@/infra/jestStreamClient/JetStreamRasterClient'
import { ILogger } from '@/infra/logger/ILogger'
import { unitConverters } from '@/utils/unit'
import { isSet } from '@/utils/validation'

export class GetWaypointAltitudeUseCase implements UseCase<Coordinates, Result<number>> {
  private httpClient: IHttpClient
  private jetStreamTerrainCLient: JetStreamRasterClient
  private logger: ILogger
  private attempts = 0

  constructor() {
    const injectionContainer = InversionContainer.getInstance().getContainer()
    this.httpClient = injectionContainer.get<IHttpClient>(InjectionTokens.HttpClient)
    this.jetStreamTerrainCLient = injectionContainer.get<JetStreamRasterClient>(InjectionTokens.JetStreamRasterClient)
    this.logger = injectionContainer.get<ILogger>(InjectionTokens.Logger)
  }

  async execute(request: Coordinates): Promise<Result<number>> {
    try {
      const { latitude, longitude } = request
      const altitudeResult = await this.getAltitudeFromJetStream(latitude, longitude)
      if (altitudeResult.isFailure) return Result.fail('Failed to get altitude')

      return Result.ok(altitudeResult.getValue())
    } catch (error) {
      this.logger.error('GetWaypointAltitudeUseCase', String(error))
      return Result.fail('An error occurred while getting the altitude')
    }
  }

  private async getAltitudeFromJetStream(latitude: number, longitude: number): Promise<Result<number>> {
    let result: Result<{ data: number[] }>

    do {
      result = await this.jetStreamTerrainCLient.getData({ longitude, latitude })
      this.attempts++
      if (!result.isFailure && !!result.getValue().data && isSet(result.getValue().data[0])) {
        this.attempts = 0
        break
      }
      this.logger.error(
        'GetWaypointAltitudeUseCase',
        `Attempt ${this.attempts}: Failed to get altitude from JetStream: ${result.error}`
      )
    } while (this.attempts < 5)

    if (result.isFailure) {
      return Result.fail('Failed to get altitude from JetStream after multiple attempts')
    }

    if (!result.getValue().data || result.getValue().data[0] === undefined || result.getValue().data[0] === null) {
      return Result.fail('No data returned from JetStream')
    }

    const elevation = result.getValue().data[0]

    return Result.ok(unitConverters.altitude(elevation, 'm', true))
  }
}
